import { Category } from '@modules/husleie-framework/src/enums/Category.enum';
import { UserAppellationKey } from '@modules/husleie-framework/src/enums/UserAppellation.enum';
import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import { Contract } from '@modules/husleie-framework/src/types/Contract.entity';
import { RentalAdjustmentTypeEnum } from '@modules/husleie-framework/src/types/ContractRentalAdjustment.entity';
import { LandlordTypeEnum } from '@modules/husleie-framework/src/types/Landlord.entity';
import { LimitedRentDurationEnum, RentDurationEnum } from '@modules/husleie-framework/src/types/Rent.entity';
import { RentalObjectFurnishingStandardEnum } from '@modules/husleie-framework/src/types/RentalObject.entity';
import {
  SignMethodEnum,
  SigningStatusEnum,
  SignerTypeEnum,
  SignerStatusEnum,
} from '@modules/husleie-framework/src/types/Signing.entity';
import { UserRoleEnum } from '@modules/husleie-framework/src/types/User.entity';
import type {
  ContractAPIInterface,
  ContractCreate,
  ContractCreateResponse,
  ContractDownload,
  ContractDownloadResponse,
  ContractFeedback,
  ContractGet,
  ContractGetResponse,
  ContractPreview,
  ContractPreviewResponse,
  ContractSigningMethodGet,
  ContractSigningMethodGetResponse,
  ContractReadyForSigning,
  ContractReadyForSigningResponse,
  ContractUpdate,
  ContractUpdateResponse,
  ContractPendingSignaturesGet,
  ContractPendingSignaturesGetResponse,
} from '@modules/store/contract/types/ContractAPI.interface';

export default class API implements ContractAPIInterface {
  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  getContract(get: ContractGet): Promise<ContractGetResponse> {
    const data: Contract = {
      uuid: '3fdf4ce8-f229-4fa1-817e-e31c4e0ca127',
      status: SigningStatusEnum.SIGNING_LANDLORD,
      landlord: {
        type: LandlordTypeEnum.PRIVATE,
        id: 37,
        user: {
          firstName: 'Example',
          lastName: 'Landlord',
          fullName: 'Example Landlord',
          email: 'example@landlord.com',
          role: UserRoleEnum.LANDLORD,
          appellation: UserAppellationKey.MSS,
        },
      },
      tenant: {
        user: {
          id: 10,
          firstName: 'Milli',
          lastName: 'Vanilli',
          fullName: 'Milli Vanilli',
          email: 'milli.vanilli@hotshots.com',
          dateOfBirth: '01.01.2000',
          appellation: UserAppellationKey.MR,
          role: UserRoleEnum.TENANT,
        },
        address: {
          street: 'M Street',
          postalCode: '11111',
          postalArea: 'M City',
          country: {
            code: 'XX',
          },
        },
      },
      tenantFeedback: '',
      coTenants: [],
      amountOfResidents: 1,
      rentalObject: {
        category: Category.APARTMENT,
        furnishingStandard: RentalObjectFurnishingStandardEnum.FURNISHED,
        rooms: [],
        furniture: [],
        storageDetail: [],
        keychain: {
          keys: [],
        },
        houseRulesUploads: [],
        additionalDocuments: [],
        useDefaultHouseRules: false,
        parkingFacilities: false,
      },
      rentalAdjustment: {
        rentalAdjustment: RentalAdjustmentTypeEnum.COMPARABLE_RENT,
        rentRegulation: false,
        tiers: [],
        firstUsedAndRented: true,
        renovationsLastThreeYears: true,
        occupancyAfterRenovations: true,
      },
      deposit: {
        ownDefinedAmount: 1000,
        ownDefined: false,
        months: 3,
        depositSolutions: [],
      },
      rentalCalculation: {
        additionalCost: 'combined',
        additionalCostTypes: false,
        utilityCostBilling: 'calendarYear',
        utilityItems: [],
        heatingCostBilling: 'calendarYear',
      },
      sign: SignMethodEnum.DIGITAL,
      rent: {
        durationType: RentDurationEnum.UNLIMITED,
        limitedRentDurationType: LimitedRentDurationEnum.OWN_USE,
      },
      mode: 'created',
    };

    LogOperations.debug('API|MOCK contract::GET Contract response', data);

    return new Promise(resolve => resolve({ contract: data }));
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  createContract(create: ContractCreate): Promise<ContractCreateResponse> {
    const data: Contract = {
      uuid: '3fdf4ce8-f229-4fa1-817e-e31c4e0ca127',
      status: SigningStatusEnum.OPEN,
      landlord: {
        type: LandlordTypeEnum.PRIVATE,
        id: 37,
      },
      tenant: {
        user: {
          id: 10,
          firstName: 'Milli',
          lastName: 'Mocatti',
          fullName: 'Milli Mocatti',
          role: UserRoleEnum.TENANT,
        },
      },
      coTenants: [],
      amountOfResidents: 1,
      rentalObject: {
        category: Category.APARTMENT,
        furnishingStandard: RentalObjectFurnishingStandardEnum.FURNISHED,
        rooms: [],
        furniture: [],
        storageDetail: [],
        keychain: {
          keys: [],
        },
        houseRulesUploads: [],
        additionalDocuments: [],
        useDefaultHouseRules: false,
        parkingFacilities: false,
      },
      rentalCalculation: {
        additionalCost: 'combined',
        additionalCostTypes: false,
        utilityCostBilling: 'calendarYear',
        utilityItems: [],
        heatingCostBilling: 'calendarYear',
      },
      rentalAdjustment: {
        rentalAdjustment: RentalAdjustmentTypeEnum.GRADUATED_RENT,
        rentRegulation: false,
        tiers: [],
        firstUsedAndRented: true,
        renovationsLastThreeYears: true,
        occupancyAfterRenovations: true,
      },
      deposit: {
        ownDefinedAmount: 1000,
        ownDefined: false,
        months: 3,
        depositSolutions: [],
      },
      sign: SignMethodEnum.DIGITAL,
      rent: {
        durationType: RentDurationEnum.UNLIMITED,
        limitedRentDurationType: LimitedRentDurationEnum.OWN_USE,
      },
      mode: 'created',
    };

    LogOperations.debug('API|MOCK contract::POST Contract response', data);

    return new Promise(resolve => resolve({ contract: data }));
  }

  updateContract(update: ContractUpdate): Promise<ContractUpdateResponse> {
    const data: Contract = update.contract;

    LogOperations.debug('API|MOCK contract::POST Contract request', data);

    return new Promise(resolve => resolve({ contract: data }));
  }

  fetchContractPreview(preview: ContractPreview): Promise<ContractPreviewResponse> {
    const data =
      preview.format === 'html'
        ? {
            html: `<h1>Mocked preview</h1><br>We are in MOCKED mode, the real preview will show here in production mode.`,
          }
        : {
            pdf: new Blob(['testing'], { type: 'application/pdf' }),
          };
    LogOperations.debug('API|MOCK contract::GET Preview', data);

    if (preview.format === 'html') {
      return new Promise(resolve => {
        setTimeout(() => resolve(data), 2000);
      });
    } else {
      return Promise.resolve(data);
    }
  }

  // eslint-disable-next-line no-unused-vars
  getContractDownloadUrl(download: ContractDownload): Promise<ContractDownloadResponse> {
    const data = { url: 'https://smartmiete.de/docs/hausordnung.pdf' };

    LogOperations.debug('API|MOCK contract::GET ContractDownloadUrl response', data);

    return Promise.resolve(data);
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  sendContractForTenantSigning(sign: ContractReadyForSigning): Promise<ContractReadyForSigningResponse> {
    const request = {
      callbacks: {
        landlord: {},
        tenant: {},
      },
      signingMethod: SignMethodEnum.DIGITAL,
    };
    const data: ContractReadyForSigningResponse = { status: SigningStatusEnum.SIGNING_TENANT };

    LogOperations.debug('API|MOCK contract::POST sendContractForTenantSigning request', request);
    LogOperations.debug('API|MOCK contract::POST sendContractForTenantSigning response', data);

    return Promise.resolve(data);
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  async getContractSigningMethod(get: ContractSigningMethodGet): Promise<ContractSigningMethodGetResponse> {
    const data = {
      signingMethod: 'token',
      signingStatus: {
        landlord: SignerStatusEnum.SIGNED,
        tenant: SignerStatusEnum.SIGNED,
      },
    };

    LogOperations.debug('API|MOCK contract::GET ContractSigningMethod request', get);
    LogOperations.debug('API|MOCK contract::GET ContractSigningMethod response', data);

    return Promise.resolve(data);
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  getContractPendingSignatures(get: ContractPendingSignaturesGet): Promise<ContractPendingSignaturesGetResponse> {
    const type = window.location.href.indexOf('/landlord/') > -1 ? SignerTypeEnum.LANDLORD : SignerTypeEnum.TENANT;

    const data = {
      status: type === SignerTypeEnum.LANDLORD ? SigningStatusEnum.SIGNING_LANDLORD : SigningStatusEnum.SIGNING_TENANT,
      uploadToken: 'upload-token',
      signers: [
        {
          uuid: '1-38738-378376877837',
          fullName: 'Example landlord',
          type: SignerTypeEnum.LANDLORD,
          status: SignerStatusEnum.PENDING,
          signature: {},
        },
        {
          uuid: '2-38738-378376877837',
          fullName: 'Example tenant',
          type: SignerTypeEnum.TENANT,
          status: SignerStatusEnum.PENDING,
          signature: {},
        },
        {
          uuid: '3-38738-378376877837',
          fullName: 'Example co-tenant',
          type: SignerTypeEnum.TENANT,
          status: SignerStatusEnum.PENDING,
          signature: {},
        },
      ],
    };

    LogOperations.debug('API|MOCK contract::GET ContractPendingSignatures request', get);
    LogOperations.debug('API|MOCK contract::GET ContractPendingSignatures response', data);

    return Promise.resolve(data);
  }

  async signContract(contractId: string, signatureId: string, fileToken: string): Promise<boolean> {
    const request = { signatureFileToken: fileToken };

    LogOperations.debug('API|MOCK contract::POST signContract request', request);
    LogOperations.debug('API|MOCK contract::POST signContract response', {});

    return true;
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  async confirmOfflineContractSigning(contractId: string): Promise<void> {
    LogOperations.debug('API|MOCK contract::POST confirmOfflineContractSigning');

    return Promise.resolve();
  }

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  sendContractTenantFeedback(feedback: ContractFeedback): Promise<void> {
    LogOperations.debug('API|MOCK contract::POST sendContractTenantFeedback', feedback);

    return Promise.resolve();
  }

  async updateContractTenantDetails(update: ContractUpdate): Promise<ContractUpdateResponse> {
    const data = { contract: update.contract };

    LogOperations.debug('API|MOCK contract::POST updateContractTenantDetails', data);

    return Promise.resolve(data);
  }
}
